import React, { useEffect, useState, useCallback, useRef } from 'react';
import './ProjectSelectorV2.css';

interface ProjectCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AvailableProject {
  id: number;
  name: string;
  category: string;
  description: string;
  icon: string;
  owner_type: string;
  is_selected: boolean;
  percentage: number;
  hours: number;
  target_hours: number;
  change_percentage: number;
  change_type: string;
  start_date?: string;
  end_date?: string;
}

interface ProjectSelectorV2Props {
  isOpen: boolean;
  onClose: () => void;
  onProjectAdded: () => void;
  authenticatedFetch?: (url: string, options?: RequestInit) => Promise<Response>;
}

const ProjectSelectorV2: React.FC<ProjectSelectorV2Props> = ({ isOpen, onClose, onProjectAdded, authenticatedFetch }) => {
  const [categories, setCategories] = useState<ProjectCategory[]>([]);
  const [projects, setProjects] = useState<AvailableProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedProject, setSelectedProject] = useState<AvailableProject | null>(null);
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isOngoing, setIsOngoing] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    icon: '📁',
    category_id: 0
  });
  const [location, setLocation] = useState<{latitude: number; longitude: number} | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const fetchingCategoriesRef = useRef(false); // 防止重复请求分类
  const fetchingProjectsRef = useRef(false); // 防止重复请求项目

  const fetchCategories = useCallback(async () => {
    // 防止重复请求
    if (fetchingCategoriesRef.current) {
      return;
    }
    fetchingCategoriesRef.current = true;
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/categories');
      const data = await res.json();
      if (data.success) {
        setCategories(data.data);
        // 默认选择"全部"分类
        setSelectedCategory('all');
      }
    } catch (error) {
      console.error('获取分类列表失败:', error);
    } finally {
      fetchingCategoriesRef.current = false;
    }
  }, [authenticatedFetch]);

  const fetchAvailableProjects = useCallback(async () => {
    // 防止重复请求
    if (fetchingProjectsRef.current) {
      return;
    }
    fetchingProjectsRef.current = true;
    
    setLoading(true);
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/me/projects/available');
      const data = await res.json();
      setProjects(data);
    } catch (error) {
      console.error('获取项目列表失败:', error);
    } finally {
      setLoading(false);
      fetchingProjectsRef.current = false;
    }
  }, [authenticatedFetch]);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setMessage('您的浏览器不支持地理位置功能');
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        setMessage(`位置获取成功：纬度 ${latitude.toFixed(6)}, 经度 ${longitude.toFixed(6)}`);
        setLocationLoading(false);
      },
      (error) => {
        console.error('获取位置失败:', error);
        let errorMessage = '获取位置失败';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = '用户拒绝了位置请求';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = '位置信息不可用';
            break;
          case error.TIMEOUT:
            errorMessage = '获取位置超时';
            break;
        }
        setMessage(errorMessage);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchAvailableProjects();
      // 设置默认开始时间为当前时间（东八区）
      const now = new Date();
      // 获取东八区时间（UTC+8）
      const utc8Time = new Date(now.getTime() + (8 * 60 * 60 * 1000));
      setStartDate(utc8Time.toISOString().slice(0, 16));
      // 自动获取地理位置
      getCurrentLocation();
    }
  }, [isOpen, authenticatedFetch]); // 直接依赖 authenticatedFetch，避免函数重新创建导致的重复执行

  // 根据选中的分类过滤项目，并排除已选中的项目
  const getFilteredProjects = () => {
    // 首先过滤掉已选中的项目
    const unselectedProjects = projects.filter(project => !project.is_selected);
    
    if (selectedCategory === 'all') {
      return unselectedProjects.filter(project => project.owner_type === 'system');
    } else {
      const category = categories.find(cat => cat.id.toString() === selectedCategory);
      if (category && category.name === '自定义') {
        // 自定义分类显示用户创建的项目
        return unselectedProjects.filter(project => project.owner_type === 'user');
      } else {
        // 其他分类显示系统项目
        return unselectedProjects.filter(project => 
          category && project.category === category.name && project.owner_type === 'system'
        );
      }
    }
  };

  const filteredProjects = getFilteredProjects();

  const handleProjectClick = (project: AvailableProject) => {
    setSelectedProject(project);
    setShowTimeForm(true);
  };

  const handleAddProject = async () => {
    if (!selectedProject) return;
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/me/add_project', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: selectedProject.id,
          percentage: 0,
          hours: 0,
          target_hours: selectedProject.target_hours || 0,
          change_percentage: 0,
          change_type: 'stable',
          start_date: startDate,
          end_date: isOngoing ? null : endDate,
          is_active: true
        })
      });

      if (res.ok) {
        setMessage('项目添加成功！');
        setShowTimeForm(false);
        setSelectedProject(null);
        onProjectAdded();
        // 刷新项目列表
        await fetchAvailableProjects();
      } else {
        const errorData = await res.json();
        setMessage(`添加失败：${errorData.detail || '请重试'}`);
      }
    } catch (error) {
      console.error('添加项目失败:', error);
      setMessage('网络错误，请重试');
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name || !newProject.category_id) {
      setMessage('请填写项目名称和选择分类');
      return;
    }

    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction('/api/me/projects/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newProject)
      });

      if (res.ok) {
        setMessage('项目创建成功！');
        setShowCreateForm(false);
        setNewProject({ name: '', description: '', icon: '📁', category_id: 0 });
        // 刷新项目列表
        await fetchAvailableProjects();
      } else {
        const errorData = await res.json();
        setMessage(`创建失败：${errorData.detail || '请重试'}`);
      }
    } catch (error) {
      console.error('创建项目失败:', error);
      setMessage('网络错误，请重试');
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    if (!window.confirm('确定要删除这个项目吗？')) return;

    try {
      const fetchFunction = authenticatedFetch || fetch;
      const res = await fetchFunction(`/api/me/projects/custom/${projectId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setMessage('项目删除成功！');
        // 刷新项目列表
        await fetchAvailableProjects();
      } else {
        const errorData = await res.json();
        setMessage(`删除失败：${errorData.detail || '请重试'}`);
      }
    } catch (error) {
      console.error('删除项目失败:', error);
      setMessage('网络错误，请重试');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="project-selector-overlay">
      <div className="project-selector-modal">
        <div className="selector-header">
          <div className="header-left">
            <h3>选择项目</h3>
            <div className="location-info">
              <span className="location-icon">📍</span>
              <span className="location-text">
                {locationLoading ? '定位中...' :
                  location ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}` :
                  message && message.startsWith('获取位置失败') ? message : ''}
              </span>
            </div>
          </div>
          <div className="header-right">
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {showTimeForm ? (
          <div className="time-form">
            <h4>设置项目时间</h4>
            <div className="form-group">
              <label>项目：{selectedProject?.name}</label>
            </div>
            <div className="form-group">
              <label>开始时间：</label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={isOngoing}
                  onChange={(e) => setIsOngoing(e.target.checked)}
                />
                一直持续
              </label>
            </div>
            {!isOngoing && (
              <div className="form-group">
                <label>结束时间：</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="form-actions">
              <button onClick={handleAddProject} className="add-btn">添加项目</button>
              <button onClick={() => setShowTimeForm(false)} className="cancel-btn">取消</button>
            </div>
          </div>
        ) : showCreateForm ? (
          <div className="create-form">
            <h4>创建新项目</h4>
            <div className="form-group">
              <label>项目名称：</label>
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                placeholder="请输入项目名称"
                required
              />
            </div>
            <div className="form-group">
              <label>项目描述：</label>
              <textarea
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                placeholder="请输入项目描述"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>项目图标：</label>
              <input
                type="text"
                value={newProject.icon}
                onChange={(e) => setNewProject({...newProject, icon: e.target.value})}
                placeholder="📁"
              />
            </div>
            <div className="form-group">
              <label>选择分类：</label>
              <select
                value={newProject.category_id}
                onChange={(e) => setNewProject({...newProject, category_id: Number(e.target.value)})}
                required
              >
                <option value={0}>请选择分类</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button onClick={handleCreateProject} className="create-btn">创建项目</button>
              <button onClick={() => setShowCreateForm(false)} className="cancel-btn">取消</button>
            </div>
          </div>
        ) : (
          <div className="selector-content">
            {/* 左侧分类列表 */}
            <div className="category-sidebar">
              <div className="category-header">
                <h4>分类</h4>
              </div>
              <div className="category-list">
                {/* 全部项目 */}
                <div
                  className={`category-item ${selectedCategory === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('all')}
                >
                  <span className="category-icon">📋</span>
                  <span className="category-name">全部项目</span>
                </div>
                
                {/* 系统分类 */}
                {categories.map(category => (
                  <div
                    key={category.id}
                    className={`category-item ${selectedCategory === category.id.toString() ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category.id.toString())}
                  >
                    <span className="category-icon">{category.icon}</span>
                    <span className="category-name">{category.name}</span>
                  </div>
                ))}
                

              </div>
            </div>

            {/* 右侧项目列表 */}
            <div className="project-content">
              <div className="project-header">
                <h4>
                  {selectedCategory === 'all' ? '全部项目' :
                   categories.find(cat => cat.id.toString() === selectedCategory)?.name || '项目'}
                </h4>
                <div className="project-actions">
                  {categories.find(cat => cat.id.toString() === selectedCategory)?.name === '自定义' && (
                    <button 
                      className="create-project-btn"
                      onClick={() => setShowCreateForm(true)}
                    >
                      + 新建项目
                    </button>
                  )}
                  <span className="project-count">{filteredProjects.length} 个项目</span>
                </div>
              </div>
              <div className="project-grid">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner">⏳</div>
                    <div className="loading-text">加载中...</div>
                  </div>
                ) : (
                  <>
                    {filteredProjects.map(project => (
                      <div 
                        key={project.id} 
                        className={`project-item ${project.is_selected ? 'selected' : ''}`}
                        onClick={() => handleProjectClick(project)}
                      >
                        <div className="project-icon">{project.icon}</div>
                        <div className="project-name">{project.name}</div>
                        <div className="project-desc">{project.description}</div>
                        {project.owner_type === 'user' && (
                          <div className="project-owner">我的项目</div>
                        )}
                        {project.is_selected && (
                          <div className="selected-badge">已选择</div>
                        )}
                        {project.owner_type === 'user' && !project.is_selected && (
                          <button
                            className="delete-btn"
                            onClick={e => { e.stopPropagation(); handleDeleteProject(project.id); }}
                          >
                            删除
                          </button>
                        )}
                      </div>
                    ))}
                    {filteredProjects.length === 0 && (
                      <div className="empty-state">
                        <div className="empty-icon">📭</div>
                        <div className="empty-text">
                          {categories.find(cat => cat.id.toString() === selectedCategory)?.name === '自定义' ? '暂无自定义项目' : '该分类暂无项目'}
                        </div>
                        {categories.find(cat => cat.id.toString() === selectedCategory)?.name === '自定义' && (
                          <button 
                            className="create-first-btn"
                            onClick={() => setShowCreateForm(true)}
                          >
                            创建第一个项目
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {message && <div className="selector-message">{message}</div>}
      </div>
    </div>
  );
};

export default ProjectSelectorV2; 